<template>
  <div class="bereich-create">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>Bereich erstellen</h2>
      <BackButton :to="'/teams'" />
    </div>

    <div class="card">
      <div class="card-body">
        <form @submit.prevent="submitForm">
          <div class="mb-3">
            <label for="teamName" class="form-label d-flex align-items-center">Bereichsname <span class="badge bg-danger ms-2">Pflichtfeld</span></label>
            <input
              type="text"
              class="form-control"
              id="teamName"
              v-model="form.name"
              :class="{ 'is-invalid': errors.name }"
              required
            />
            <div class="invalid-feedback" v-if="errors.name">{{ errors.name }}</div>
          </div>

          <div class="mb-3">
            <label for="teamDescription" class="form-label">Beschreibung</label>
            <textarea
              class="form-control"
              id="teamDescription"
              v-model="form.description"
              rows="3"
            ></textarea>
            <div class="form-text">Beschreibe deinen Bereich kurz.</div>
          </div>

          <div class="mb-4">
            <h5>Quittungsnummer-Format</h5>
            <p class="text-muted small">
              Definiere das Format für die Quittungsnummern in diesem Bereich.
            </p>
            
            <div class="alert alert-light border">
              <button 
                class="btn btn-link p-2 text-decoration-none d-flex align-items-center w-100" 
                type="button" 
                @click="receiptFormatExpanded = !receiptFormatExpanded"
                style="border-radius: 0.375rem;"
              >
                <font-awesome-icon icon="info-circle" class="me-2" />
                <h6 class="mb-0">Was ist ein Quittungsnummer-Format?</h6>
                <font-awesome-icon 
                  :icon="receiptFormatExpanded ? 'chevron-up' : 'chevron-down'" 
                  class="ms-auto" 
                />
              </button>
              <div v-show="receiptFormatExpanded" class="mt-2">
                <p class="mb-2 small">
                  Das Quittungsnummer-Format bestimmt, wie die automatischen Nummern für Quittungen in deinem Bereich generiert werden. 
                  Jede Quittung erhält eine eindeutige Nummer basierend auf dem gewählten Format.
                </p>
                <ul class="small mb-0">
                  <li><strong>Präfix:</strong> Ein optionaler Buchstaben-/Zahlenkombination (max. 3 Zeichen), der vor jeder Nummer steht</li>
                  <li><strong>Nummerierungsformat:</strong> Bestimmt, ob und wie Datumskomponenten in die Nummer eingebaut werden</li>
                </ul>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-6">
                <label for="receiptPrefix" class="form-label">Präfix (optional)</label>
                <input
                  type="text"
                  class="form-control"
                  id="receiptPrefix"
                  v-model="form.receiptNumberPrefix"
                  maxlength="3"
                  placeholder="z.B. ABC"
                  style="text-transform: uppercase"
                  @input="form.receiptNumberPrefix = $event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')"
                />
                <div class="form-text">Maximal 3 Großbuchstaben oder Zahlen</div>
              </div>
              <div class="col-md-6">
                <label for="receiptFormat" class="form-label">Nummerierungsformat</label>
                <select
                  class="form-select"
                  id="receiptFormat"
                  v-model="form.receiptNumberFormat"
                >
                  <option value="sequential">Fortlaufende Nummern (z.B. -1, -2, -3...)</option>
                  <option value="year_sequential">Jahr + fortlaufend (z.B. 2025-1, jedes Jahr bei 1 startend)</option>
                  <option value="year_month_sequential">Jahr + Monat + fortlaufend (z.B. 202501-1, jeden Monat bei 1 startend)</option>
                  <option value="year_month_day_sequential">Jahr + Monat + Tag + fortlaufend (z.B. 20250101-1, jeden Tag bei 1 startend)</option>
                </select>
              </div>
            </div>

            <div class="alert alert-info">
              <small>
                <strong>Beispiel:</strong> 
                <span v-if="form.receiptNumberPrefix">{{ form.receiptNumberPrefix }}</span><span v-if="getExampleNumber()">{{ getExampleNumber() }}</span><span v-if="!form.receiptNumberPrefix && !getExampleNumber()">0001</span>
              </small>
            </div>
          </div>

          <div class="mb-4">
            <h5>Mitgliederfelder</h5>
            <p class="text-muted small">
              Definiere die Felder, die für jedes Bereichsmitglied erforderlich sind.
            </p>
            
            <div class="alert alert-light border">
              <button 
                class="btn btn-link p-2 text-decoration-none d-flex align-items-center w-100" 
                type="button" 
                @click="schemaExpanded = !schemaExpanded"
                style="border-radius: 0.375rem;"
              >
                <font-awesome-icon icon="info-circle" class="me-2" />
                <h6 class="mb-0">Was ist ein Mitgliederfelder?</h6>
                <font-awesome-icon 
                  :icon="schemaExpanded ? 'chevron-up' : 'chevron-down'" 
                  class="ms-auto" 
                />
              </button>
              <div v-show="schemaExpanded" class="mt-2">
                <p class="mb-2 small">
                  Im Mitgliederfelder sind Mitgliederfelder definiert, das sind Informationen, die ein Admin von jedem Bereichs-Mitglied abfragt. 
                  Diese können optional oder verpflichtend sein. Du kannst verschiedene Feldtypen erstellen und festlegen, 
                  welche Felder Pflichtfelder sind.
                </p>
                <ul class="small mb-0">
                  <li><strong>Feldname:</strong> Der Titel des Feldes (z.B. "Geburtsdatum", "Abteilung")</li>
                  <li><strong>Feldtyp:</strong> Bestimmt die Art der Eingabe (Text, E-Mail, Datum, etc.)</li>
                  <li><strong>Pflichtfeld:</strong> Legt fest, ob das Feld ausgefüllt werden muss</li>
                  <li><strong>Beschreibung:</strong> Hilfstext für das Feld (bei Checkboxen wird dies als Label verwendet)</li>
                  <li><strong>Platzhalter:</strong> Beispieltext, der im Eingabefeld angezeigt wird</li>
                </ul>
              </div>
            </div>

            <div v-for="(field, index) in form.fields" :key="index" class="card mb-2">
              <div class="card-body">
                <div class="d-flex justify-content-between mb-2">
                  <h6>Feld {{ index + 1 }}</h6>
                  <div class="d-flex flex-row">
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-secondary me-1"
                      @click="moveField(index, -1)"
                      :disabled="index === 0"
                    >
                      <font-awesome-icon icon="arrow-up" />
                    </button>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-secondary me-1"
                      @click="moveField(index, 1)"
                      :disabled="index === form.fields.length - 1"
                    >
                      <font-awesome-icon icon="arrow-down" />
                    </button>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-danger"
                      @click="removeField(index)"
                    >
                      <font-awesome-icon icon="trash" />
                    </button>
                  </div>
                </div>
                
                <div class="row">
                  <div class="col-md-4">
                    <label :for="`fieldName${index}`" class="form-label">Feldname</label>
                    <input
                      type="text"
                      class="form-control"
                      :id="`fieldName${index}`"
                      v-model="field.name"
                      placeholder="z.B. Geburtsdatum"
                      :class="{ 'is-invalid': errors[`field-${index}-name`] }"
                    />
                    <div class="invalid-feedback" v-if="errors[`field-${index}-name`]" >
                      {{ errors[`field-${index}-name`] }}
                    </div>
                  </div>
                  <div class="col-md-4">
                    <label :for="`fieldType${index}`" class="form-label">Feldtyp</label>
                    <select class="form-select" :id="`fieldType${index}`" v-model="field.type">
                      <option value="text">Text</option>
                      <option value="email">E-Mail</option>
                      <option value="number">Zahl</option>
                      <option value="tel">Telefon</option>
                      <option value="date">Datum</option>
                      <option value="checkbox">Checkbox</option>
                      <option value="select">Auswahl</option>
                    </select>
                  </div>
                  <div class="col-md-4">
                    <div class="form-label">Pflichtfeld</div>
                    <div class="form-check form-switch form-switch-lg">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        :id="`fieldRequired${index}`"
                        v-model="field.required"
                        role="switch"
                      />
                      <label class="form-check-label" :for="`fieldRequired${index}`">
                        {{ field.required ? 'Ja' : 'Nein' }}
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Beschreibung und Platzhalter -->
                <div class="row g-2 mt-2">
                  <div class="col-md-6">
                    <label :for="`fieldDescription${index}`" class="form-label">
                      Beschreibung
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-secondary ms-2" 
                        @click="addLinkToDescription(index)"
                        title="Link zur Beschreibung hinzufügen"
                      >
                        <font-awesome-icon icon="link" />
                      </button>
                    </label>
                    <textarea
                      class="form-control"
                      :id="`fieldDescription${index}`"
                      v-model="field.description"
                      rows="2"
                      placeholder="Optionale Beschreibung für das Feld"
                    ></textarea>
                    <div class="form-text">
                      <small>
                        <strong>Hinweis:</strong> Bei Checkbox-Feldern wird die Beschreibung als Label verwendet.
                        Links können mit dem Link-Button hinzugefügt werden.
                      </small>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label :for="`fieldPlaceholder${index}`" class="form-label">Platzhalter</label>
                    <input
                      type="text"
                      class="form-control"
                      :id="`fieldPlaceholder${index}`"
                      v-model="field.placeholder"
                      placeholder="Platzhaltertext für das Eingabefeld"
                    />
                    <div class="form-text">
                      <small>Wird nur bei Text-, E-Mail-, Zahl- und Datumsfeldern angezeigt</small>
                    </div>
                  </div>
                </div>
                
                <div v-if="field.type === 'select'" class="mt-2">
                  <label :for="`fieldOptions${index}`" class="form-label">Optionen</label>
                  <textarea
                    class="form-control"
                    :id="`fieldOptions${index}`"
                    v-model="field.options"
                    rows="2"
                    placeholder="Option 1&#10;Option 2&#10;Option 3"
                  ></textarea>
                  <div class="form-text">
                    Gib jede Option in eine neue Zeile ein.
                  </div>
                </div>
              </div>
            </div>

            <button type="button" class="btn btn-outline-primary" @click="addField">
              <font-awesome-icon icon="plus" /> Feld hinzufügen
            </button>
          </div>

          <div v-if="error" class="alert alert-danger mb-3" role="alert">
            <font-awesome-icon icon="exclamation-triangle" /> {{ error }}
          </div>

          <div class="d-flex justify-content-between">
            <button type="button" class="btn btn-outline-secondary" @click="goBack">
              Abbrechen
            </button>
            <button type="submit" class="btn btn-primary" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Erstellen
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { teamService } from '@/api/teams'
import { useTeamsStore } from '@/stores/teams'
import { showError, showSuccess } from '@/services/notification'
import Swal from 'sweetalert2'

const router = useRouter()
const teamsStore = useTeamsStore()


const { selectTeam, fetchTeams } = teamsStore




const loading = ref(false)
const error = ref('')


const receiptFormatExpanded = ref(false)
const schemaExpanded = ref(false)

const form = reactive({
  name: '',
  description: '',
  receiptNumberPrefix: '',
  receiptNumberFormat: 'sequential',
  fields: []
})
    
const errors = reactive({
  name: ''
})
    

const addField = () => {
  const newIndex = form.fields.length
  form.fields.push({
    name: '',
    type: 'text',
    required: false,
    options: '',
    description: '',
    placeholder: ''
  })
  errors[`field-${newIndex}-name`] = ''
}

const addLinkToDescription = async (index) => {
  const field = form.fields[index]
  const result = await Swal.fire({
    title: 'Link hinzufügen',
    html: `
      <div class="mb-3">
        <label for="linkText" class="form-label">Link-Text:</label>
        <input type="text" id="linkText" class="form-control" placeholder="z.B. Datenschutzerklärung">
      </div>
      <div class="mb-3">
        <label for="linkUrl" class="form-label">URL:</label>
        <input type="url" id="linkUrl" class="form-control" placeholder="https://example.com">
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: 'Hinzufügen',
    cancelButtonText: 'Abbrechen',
    preConfirm: () => {
      const linkText = document.getElementById('linkText').value.trim()
      const linkUrl = document.getElementById('linkUrl').value.trim()
      
      if (!linkText || !linkUrl) {
        Swal.showValidationMessage('Bitte fülle beide Felder aus')
        return false
      }
      
      if (!linkUrl.startsWith('https://')) {
        Swal.showValidationMessage('URL muss mit https:// beginnen')
        return false
      }
      
      return { linkText, linkUrl }
    }
  })
  
  if (result.isConfirmed) {
    const { linkText, linkUrl } = result.value
    
    
    const linkHtml = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`
    
    if (field.description) {
      field.description += ' ' + linkHtml
    } else {
      field.description = linkHtml
    }
  }
}
    
const removeField = (index) => {
  form.fields.splice(index, 1)
}

const moveField = (index, direction) => {
  const newIndex = index + direction
  if (newIndex < 0 || newIndex >= form.fields.length) return

  const fields = [...form.fields]
  const field = fields[index]
  fields[index] = fields[newIndex]
  fields[newIndex] = field
  form.fields = fields
}

const goBack = () => {
  router.push({ name: 'teams-list' })
}


const getExampleNumber = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  
  switch (form.receiptNumberFormat) {
    case 'sequential':
      return '-1'
    case 'year_sequential':
      return `${year}-1`
    case 'year_month_sequential':
      return `${year}${month}-1`
    case 'year_month_day_sequential':
      return `${year}${month}${day}-1`
    default:
      return '-1'
  }
}

const submitForm = async () => {
  errors.name = ''
  error.value = ''

  let isValid = true


  form.fields.forEach((field, index) => {
    errors[`field-${index}-name`] = ''
  })

  if (!form.name.trim()) {
    errors.name = 'Bereichsname ist erforderlich'
    isValid = false
  }

  form.fields.forEach((field, index) => {
    if (!field.name.trim()) {
      errors[`field-${index}-name`] = 'Feldname ist erforderlich'
      isValid = false
    }
  })

  if (!isValid) return

  loading.value = true

  try {
    const fields = form.fields.map((field, index) => ({
      name: field.name.trim(),
      type: field.type,
      required: field.required,
      options: field.options,
      description: field.description,
      placeholder: field.placeholder,
      order: index
    }))

    const newTeam = await teamService.createTeam({
      name: form.name.trim(),
      description: form.description.trim(),
      receiptNumberPrefix: form.receiptNumberPrefix.trim(),
      receiptNumberFormat: form.receiptNumberFormat,
      fields: fields
    })

    showSuccess('Bereich wurde erfolgreich erstellt')
    
    selectTeam(newTeam)
    await fetchTeams()

    router.push({ name: 'teams-detail', params: { id: newTeam.id } })
  } catch (err) {
    console.error('Fehler beim Erstellen des Bereichs:', err)
    const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Bereich konnte nicht erstellt werden'
    error.value = errorMessage
    showError(errorMessage)
  } finally {
    loading.value = false
  }
}
</script>
